import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query,
  runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { firestore } from '../views/firebaseConfig';
import {
  COLLECTIONS, TOURNAMENT_ENTRY_STATUS, TOURNAMENT_MATCH_STATUS, TOURNAMENT_STATUS,
  list, normalizeStatus, number, text,
} from '../contracts/p4r';

const tournamentRef = (id) => doc(firestore, COLLECTIONS.TOURNAMENTS, id);
const entryRef = (id) => doc(firestore, COLLECTIONS.TOURNAMENT_ENTRIES, id);
const matchRef = (id) => doc(firestore, COLLECTIONS.TOURNAMENT_MATCHES, id);
const activeEntry = (entry) => !['WITHDRAWN', 'NO_SHOW'].includes(normalizeStatus(entry.status));

export const mapTournament = (snapshot) => ({
  id: snapshot.id,
  ...snapshot.data(),
  status: normalizeStatus(snapshot.data().status, TOURNAMENT_STATUS.DRAFT),
  visibility: normalizeStatus(snapshot.data().visibility, 'PUBLIC'),
  entryMode: normalizeStatus(snapshot.data().entryMode, 'INDIVIDUAL'),
  maxEntries: number(snapshot.data().maxEntries, 16),
  currentEntriesCount: number(snapshot.data().currentEntriesCount),
  raceTo: number(snapshot.data().raceTo, 5),
  tableCount: number(snapshot.data().tableCount, 1),
});

export function listenPublicTournaments(onData, onError) {
  const q = query(collection(firestore, COLLECTIONS.TOURNAMENTS), orderBy('timeUpdated', 'desc'), limit(100));
  return onSnapshot(q, (snapshot) => onData(snapshot.docs.map(mapTournament).filter((item) => item.visibility === 'PUBLIC' && item.status !== 'DELETED')), onError);
}
export function listenTournament(id, onData, onError) {
  return onSnapshot(tournamentRef(id), (snapshot) => onData(snapshot.exists() ? mapTournament(snapshot) : null), onError);
}
export function listenTournamentEntries(id, onData, onError) {
  const q = query(collection(firestore, COLLECTIONS.TOURNAMENT_ENTRIES), where('tournamentId', '==', id));
  return onSnapshot(q, (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), status: normalizeStatus(item.data().status) })).sort((a,b) => number(a.seed,9999)-number(b.seed,9999))), onError);
}
export function listenTournamentMatches(id, onData, onError) {
  const q = query(collection(firestore, COLLECTIONS.TOURNAMENT_MATCHES), where('tournamentId', '==', id));
  return onSnapshot(q, (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), status: normalizeStatus(item.data().status) })).sort((a,b) => number(a.roundNumber)-number(b.roundNumber)||number(a.matchIndex)-number(b.matchIndex))), onError);
}
export function listenTournamentAnnouncements(id, onData, onError) {
  const q = query(collection(firestore, COLLECTIONS.TOURNAMENT_ANNOUNCEMENTS), where('tournamentId', '==', id));
  return onSnapshot(q, (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))), onError);
}

export async function createTournament(uid, profile, form, desiredStatus = TOURNAMENT_STATUS.OPEN) {
  const ref = doc(collection(firestore, COLLECTIONS.TOURNAMENTS));
  const startAt = form.startAt ? new Date(form.startAt) : null;
  const checkInOpenAt = form.checkInOpenAt ? new Date(form.checkInOpenAt) : null;
  const checkInCloseAt = form.checkInCloseAt ? new Date(form.checkInCloseAt) : null;
  await setDoc(ref, {
    managerUid: uid,
    managerName: text(profile?.nickname || profile?.name || profile?.displayName),
    managerPhotoUrl: text(profile?.profileImageUrl),
    title: text(form.title), description: text(form.description), bannerUrl: text(form.bannerUrl),
    visibility: normalizeStatus(form.visibility, 'PUBLIC'), status: normalizeStatus(desiredStatus, TOURNAMENT_STATUS.OPEN),
    entryMode: normalizeStatus(form.entryMode, 'INDIVIDUAL'), bracketType: 'SINGLE_ELIMINATION',
    discipline: normalizeStatus(form.discipline, 'EIGHT_BALL'), ruleset: normalizeStatus(form.ruleset, 'CUSTOM'),
    customRulesText: text(form.customRulesText), breakFormat: normalizeStatus(form.breakFormat, 'ALTERNATE_BREAK'),
    venueName: text(form.venueName || form.venueLabel), venueLabel: text(form.venueName || form.venueLabel),
    venueAddress: text(form.venueAddress), venuePlaceId: text(form.venuePlaceId), city: text(form.city), state: text(form.state), zipCode: text(form.zipCode), country: text(form.country || 'US'),
    entryFee: number(form.entryFee), prizeSummary: text(form.prizeSummary), raceTo: Math.max(1, number(form.raceTo,5)),
    maxEntries: Math.max(2, number(form.maxEntries,16)), tableCount: Math.max(1, number(form.tableCount,1)),
    allowWalkIns: form.allowWalkIns !== false, allowSelfScore: Boolean(form.allowSelfScore),
    checkInOpenAt, checkInCloseAt, startAt, streamUrl: text(form.streamUrl), contactText: text(form.contactText),
    currentEntriesCount: 0, timeCreated: serverTimestamp(), timeUpdated: serverTimestamp(),
  });
  return ref.id;
}

export async function updateTournament(id, form) {
  await updateDoc(tournamentRef(id), {
    title: text(form.title), description: text(form.description), bannerUrl: text(form.bannerUrl),
    visibility: normalizeStatus(form.visibility, 'PUBLIC'), entryMode: normalizeStatus(form.entryMode, 'INDIVIDUAL'),
    discipline: normalizeStatus(form.discipline, 'EIGHT_BALL'), ruleset: normalizeStatus(form.ruleset, 'CUSTOM'),
    customRulesText: text(form.customRulesText), breakFormat: normalizeStatus(form.breakFormat, 'ALTERNATE_BREAK'),
    venueName: text(form.venueName || form.venueLabel), venueLabel: text(form.venueName || form.venueLabel), venueAddress: text(form.venueAddress),
    city: text(form.city), state: text(form.state), zipCode: text(form.zipCode), country: text(form.country || 'US'),
    entryFee: number(form.entryFee), prizeSummary: text(form.prizeSummary), raceTo: Math.max(1, number(form.raceTo,5)),
    maxEntries: Math.max(2, number(form.maxEntries,16)), tableCount: Math.max(1, number(form.tableCount,1)),
    allowWalkIns: form.allowWalkIns !== false, allowSelfScore: Boolean(form.allowSelfScore),
    startAt: form.startAt ? new Date(form.startAt) : null,
    checkInOpenAt: form.checkInOpenAt ? new Date(form.checkInOpenAt) : null,
    checkInCloseAt: form.checkInCloseAt ? new Date(form.checkInCloseAt) : null,
    streamUrl: text(form.streamUrl), contactText: text(form.contactText), timeUpdated: serverTimestamp(),
  });
}

export async function setTournamentStatus(id, status) {
  await updateDoc(tournamentRef(id), { status: normalizeStatus(status), timeUpdated: serverTimestamp() });
}

export async function joinTournamentAsPlayer(tournament, uid, profile) {
  if (!['OPEN','CHECK_IN'].includes(tournament.status)) throw new Error('This tournament is not accepting entries.');
  if (!['INDIVIDUAL','BOTH'].includes(tournament.entryMode)) throw new Error('This tournament accepts team entries only.');
  const id = `${tournament.id}_${uid}`;
  await runTransaction(firestore, async (transaction) => {
    const [tournamentSnapshot, existingEntry] = await Promise.all([transaction.get(tournamentRef(tournament.id)), transaction.get(entryRef(id))]);
    if (!tournamentSnapshot.exists()) throw new Error('Tournament not found.');
    const data = tournamentSnapshot.data();
    const current = number(data.currentEntriesCount);
    if (current >= number(data.maxEntries,16) && !(existingEntry.exists() && activeEntry(existingEntry.data()))) throw new Error('This tournament is full.');
    const alreadyActive = existingEntry.exists() && activeEntry(existingEntry.data());
    transaction.set(entryRef(id), {
      tournamentId: tournament.id, displayName: text(profile?.nickname || profile?.name || 'Pool Player'),
      imageUrl: text(profile?.profileImageUrl), entryType: 'PLAYER', entryRefId: uid, createdBy: uid,
      memberUids: [uid], status: TOURNAMENT_ENTRY_STATUS.JOINED,
      seed: existingEntry.exists() ? existingEntry.data().seed || current + 1 : current + 1,
      checkedIn: false, checkedInAt: null, rosterSource: 'registered', timeCreated: existingEntry.data()?.timeCreated || serverTimestamp(), timeUpdated: serverTimestamp(),
    });
    if (!alreadyActive) transaction.update(tournamentRef(tournament.id), { currentEntriesCount: current + 1, timeUpdated: serverTimestamp() });
  });
  await setDoc(doc(firestore, COLLECTIONS.TOURNAMENT_USER_LINKS, uid, 'links', tournament.id), {
    tournamentId: tournament.id, role: 'PLAYER', entryId: id, tournamentTitle: tournament.title,
    tournamentBannerUrl: text(tournament.bannerUrl), timeUpdated: serverTimestamp(),
  }, { merge: true });
}

export async function withdrawTournamentEntry(tournamentId, entry, uid) {
  if (!entry || !activeEntry(entry)) return;
  await runTransaction(firestore, async (transaction) => {
    const [tournamentSnapshot, entrySnapshot] = await Promise.all([transaction.get(tournamentRef(tournamentId)), transaction.get(entryRef(entry.id))]);
    if (!entrySnapshot.exists()) return;
    transaction.update(entryRef(entry.id), { status: TOURNAMENT_ENTRY_STATUS.WITHDRAWN, checkedIn: false, timeUpdated: serverTimestamp() });
    if (tournamentSnapshot.exists()) transaction.update(tournamentRef(tournamentId), { currentEntriesCount: Math.max(0, number(tournamentSnapshot.data().currentEntriesCount)-1), timeUpdated: serverTimestamp() });
  });
  await deleteDoc(doc(firestore, COLLECTIONS.TOURNAMENT_USER_LINKS, uid, 'links', tournamentId)).catch(() => {});
}

export async function setEntryCheckedIn(entryId, checkedIn) {
  await updateDoc(entryRef(entryId), { checkedIn: Boolean(checkedIn), checkedInAt: checkedIn ? serverTimestamp() : null, timeUpdated: serverTimestamp() });
}

export async function addGuestEntry(tournament, managerUid, nickname, seed) {
  const ref = doc(collection(firestore, COLLECTIONS.TOURNAMENT_ENTRIES));
  await setDoc(ref, {
    tournamentId: tournament.id, displayName: text(nickname), imageUrl: '', entryType: 'PLAYER', entryRefId: ref.id,
    createdBy: managerUid, memberUids: [], status: TOURNAMENT_ENTRY_STATUS.JOINED, seed: number(seed, tournament.currentEntriesCount + 1),
    checkedIn: false, checkedInAt: null, rosterSource: 'guest', timeCreated: serverTimestamp(), timeUpdated: serverTimestamp(),
  });
  await updateDoc(tournamentRef(tournament.id), { currentEntriesCount: number(tournament.currentEntriesCount)+1, timeUpdated: serverTimestamp() });
  return ref.id;
}

export async function updateEntry(entryId, patch) {
  await updateDoc(entryRef(entryId), { ...patch, seed: patch.seed == null ? null : number(patch.seed), timeUpdated: serverTimestamp() });
}

export async function deleteEntry(tournamentId, entry) {
  await runTransaction(firestore, async (transaction) => {
    const t = await transaction.get(tournamentRef(tournamentId));
    transaction.delete(entryRef(entry.id));
    if (t.exists() && activeEntry(entry)) transaction.update(tournamentRef(tournamentId), { currentEntriesCount: Math.max(0, number(t.data().currentEntriesCount)-1), timeUpdated: serverTimestamp() });
  });
}

export async function randomizeSeeds(entries) {
  const active = entries.filter(activeEntry).sort(() => Math.random() - .5);
  const batch = writeBatch(firestore);
  active.forEach((entry, index) => batch.update(entryRef(entry.id), { seed: index + 1, timeUpdated: serverTimestamp() }));
  await batch.commit();
}

const nextPowerOfTwo = (value) => { let size = 1; while (size < value) size *= 2; return size; };
const seedPositions = (size) => {
  if (size === 1) return [1];
  let positions = [1, 2];
  for (let bracket = 4; bracket <= size; bracket *= 2) {
    const next = [];
    positions.forEach((seed) => { next.push(seed, bracket + 1 - seed); });
    positions = next;
  }
  return positions;
};

export async function clearBracket(tournamentId) {
  const snapshot = await getDocs(query(collection(firestore, COLLECTIONS.TOURNAMENT_MATCHES), where('tournamentId','==',tournamentId)));
  const batch = writeBatch(firestore); snapshot.docs.forEach((item) => batch.delete(item.ref)); await batch.commit();
}

export async function buildSingleElimination(tournament, entries) {
  const participants = entries.filter(activeEntry).sort((a,b)=>number(a.seed,9999)-number(b.seed,9999));
  if (participants.length < 2) throw new Error('At least two active entries are required.');
  await clearBracket(tournament.id);
  const size = nextPowerOfTwo(participants.length);
  const totalRounds = Math.log2(size);
  const bySeed = Object.fromEntries(participants.map((entry,index)=>[number(entry.seed,index+1),entry]));
  const positions = seedPositions(size);
  const slots = positions.map((seed)=>bySeed[seed] || null);
  const batch = writeBatch(firestore);
  const matches = [];
  for (let round=1; round<=totalRounds; round+=1) {
    const count = size / (2 ** round);
    for (let index=0; index<count; index+=1) {
      const id = `${tournament.id}_r${round}_m${index+1}`;
      const nextId = round < totalRounds ? `${tournament.id}_r${round+1}_m${Math.floor(index/2)+1}` : '';
      const roundLabel = round === totalRounds ? 'Final' : round === totalRounds-1 ? 'Semifinal' : round === totalRounds-2 ? 'Quarterfinal' : `Round ${round}`;
      let a=null,b=null;
      if (round===1) { a=slots[index*2]; b=slots[index*2+1]; }
      const status = round===1 && a && b ? TOURNAMENT_MATCH_STATUS.READY : TOURNAMENT_MATCH_STATUS.PENDING;
      const match = { id, tournamentId:tournament.id, roundNumber:round, matchIndex:index+1, roundLabel,
        sideAEntryId:a?.id||'', sideAName:a?.displayName||'', sideBEntryId:b?.id||'', sideBName:b?.displayName||'',
        scoreA:0, scoreB:0, winnerEntryId:'', status, nextMatchId:nextId, nextSlot:index%2===0?'A':'B', tableName:'', tableNumber:null, scheduledAt:null, timeUpdated:serverTimestamp() };
      matches.push(match); batch.set(matchRef(id), match);
    }
  }
  await batch.commit();
  // Resolve first-round byes immediately.
  for (const match of matches.filter((item)=>item.roundNumber===1)) {
    const only = match.sideAEntryId ? (!match.sideBEntryId ? {id:match.sideAEntryId,name:match.sideAName}:null) : (match.sideBEntryId ? {id:match.sideBEntryId,name:match.sideBName}:null);
    if (only) await advanceWinner(match, only.id, only.name, 0, 0, true);
  }
}

async function advanceWinner(match, winnerId, winnerName, scoreA, scoreB, walkover=false) {
  const batch = writeBatch(firestore);
  batch.update(matchRef(match.id), { scoreA:number(scoreA), scoreB:number(scoreB), winnerEntryId:winnerId, status:walkover?TOURNAMENT_MATCH_STATUS.WALKOVER:TOURNAMENT_MATCH_STATUS.FINAL, timeUpdated:serverTimestamp() });
  if (match.nextMatchId) {
    const nextSnapshot = await getDoc(matchRef(match.nextMatchId));
    if (nextSnapshot.exists()) {
      const field = match.nextSlot === 'B' ? 'sideBEntryId' : 'sideAEntryId';
      const nameField = match.nextSlot === 'B' ? 'sideBName' : 'sideAName';
      const otherField = match.nextSlot === 'B' ? 'sideAEntryId' : 'sideBEntryId';
      const nextStatus = nextSnapshot.data()[otherField] ? TOURNAMENT_MATCH_STATUS.READY : TOURNAMENT_MATCH_STATUS.PENDING;
      batch.update(matchRef(match.nextMatchId), { [field]:winnerId,[nameField]:winnerName,status:nextStatus,timeUpdated:serverTimestamp() });
    }
    batch.update(entryRef(winnerId), { status: TOURNAMENT_ENTRY_STATUS.ADVANCED, timeUpdated:serverTimestamp() });
  } else {
    batch.update(entryRef(winnerId), { status: TOURNAMENT_ENTRY_STATUS.CHAMPION, timeUpdated:serverTimestamp() });
  }
  const loserId = winnerId === match.sideAEntryId ? match.sideBEntryId : match.sideAEntryId;
  if (loserId) batch.update(entryRef(loserId), { status: TOURNAMENT_ENTRY_STATUS.ELIMINATED, timeUpdated:serverTimestamp() });
  await batch.commit();
}

export async function reportMatchResult(match, scoreA, scoreB) {
  const a = number(scoreA), b = number(scoreB);
  if (a === b) throw new Error('A final tournament match cannot end in a tie.');
  const winnerId = a > b ? match.sideAEntryId : match.sideBEntryId;
  const winnerName = a > b ? match.sideAName : match.sideBName;
  if (!winnerId) throw new Error('Both bracket sides must be assigned before reporting a result.');
  await advanceWinner(match, winnerId, winnerName, a, b, false);
}

export async function reopenMatch(match) {
  await updateDoc(matchRef(match.id), { scoreA:0, scoreB:0, winnerEntryId:'', status:TOURNAMENT_MATCH_STATUS.READY, timeUpdated:serverTimestamp() });
}

export async function assignTable(matchId, tableNumber) {
  const value = tableNumber === '' || tableNumber == null ? null : number(tableNumber);
  await updateDoc(matchRef(matchId), { tableNumber:value, tableName:value?`Table ${value}`:'', timeUpdated:serverTimestamp() });
}

export async function createTournamentAnnouncement(tournament, uid, profile, form) {
  return addDoc(collection(firestore, COLLECTIONS.TOURNAMENT_ANNOUNCEMENTS), {
    tournamentId:tournament.id,tournamentTitle:tournament.title,managerUid:uid,title:text(form.title),body:text(form.body),
    type:normalizeStatus(form.type,'GENERAL'),pinned:Boolean(form.pinned),createdByName:text(profile?.nickname||profile?.name),
    notifyScope:'PARTICIPANTS_ONLY',relatedEntryIds:list(form.relatedEntryIds),createdAt:serverTimestamp(),timeUpdated:serverTimestamp(),
  });
}
